/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html } from 'lit';
import { baseStyles, CdsBaseButton, getElementWidth, property } from '@cds/core/internal';
import styles from './button.element.scss';
export var ClrLoadingState;
(function (ClrLoadingState) {
    ClrLoadingState["default"] = "default";
    ClrLoadingState["loading"] = "loading";
    ClrLoadingState["success"] = "success";
    ClrLoadingState["error"] = "error";
})(ClrLoadingState || (ClrLoadingState = {}));
/**
 * Buttons allow an application to communicate action and direct user intent.
 *
 * ```typescript
 * import '@cds/core/button/register.js';
 * ```
 *
 * ```html
 * <cds-button>submit</cds-button>
 * ```
 *
 * @element cds-button
 * @slot - Content slot for inside the button
 * @cssprop --background
 * @cssprop --border-color
 * @cssprop --border-radius
 * @cssprop --border-width
 * @cssprop --box-shadow-color
 * @cssprop --color
 * @cssprop --font-family
 * @cssprop --font-size
 * @cssprop --font-weight
 * @cssprop --height
 * @cssprop --letter-spacing
 * @cssprop --min-width
 * @cssprop --padding
 * @cssprop --text-decoration
 * @cssprop --text-transform
 */
export class CdsButton extends CdsBaseButton {
    constructor() {
        super(...arguments);
        /**
         * Define the type of action the button triggers
         *
         * - `solid`: buttons direct the user’s attention to the primary action the application is suggesting that the user take.
         * - `outline`: buttons indicate secondary actions that compliments a primary action or reduces visual noise when there are many actions on the page.
         * - `flat`: buttons are used as tertiary buttons. Can also be used inline because they are different from content in style and recognizable as buttons alongside content.
         */
        this.action = 'solid';
        /**
         * Sets the color of the button to match the following string statuses
         */
        this.status = 'primary';
        /**
         * Sets the overall height and width of the button based on the following string values:
         */
        this.size = 'md';
        /** Sets if the button should be full width with display block */
        this.block = false;
        /**
         * Changes the button content based on the value passed.
         * @type {default | loading | success | error}
         *
         * - `default`: shows the content of the button
         * - `loading`: disables the button and shows a spinner inside the button
         * - `success`: disables the button and shows a check mark inside the button; auto-triggers to change back to DEFAULT state after 1000 ms
         * - `error`: shows the content of the button (in the context of application, this state is usually entered from a LOADING state. the application should show appropriate error message)
         */
        this.loadingState = ClrLoadingState.default;
        // when the loading state changes,
        //    the disabled state should be set back to what the consumer manually set (if they set it)
        // the setter here should never be called in the component, call super instead
        //  https://github.com/vmware-clarity/core/issues/129
        this._disabledExternally = false;
    }
    get disabled() {
        return super.disabled;
    }
    set disabled(value) {
        this._disabledExternally = value;
        super.disabled = value;
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        if (!this.isDefaultLoadingState(this.loadingState)) {
            super.disabled = true;
        }
    }
    update(props) {
        if (props.has('loadingState') && props.get('loadingState') !== undefined) {
            if (this.isDefaultLoadingState(this.loadingState)) {
                this.restoreButton();
            }
            else {
                this.disableButton();
            }
        }
        super.update(props);
    }
    render() {
        return html `<div class="private-host" cds-layout="horizontal gap:xs wrap:none align:center">
      ${this.loadingState === ClrLoadingState.success
            ? html `<cds-icon shape="check" status="success" size="18"></cds-icon>`
            : ''}
      ${this.loadingState === ClrLoadingState.error
            ? html `<cds-icon shape="error-standard" status="danger" size="18"></cds-icon>`
            : ''}
      ${this.loadingState === ClrLoadingState.loading
            ? html `<cds-progress-circle .size=${this.size === 'sm' ? '12' : '18'} status="info"></cds-progress-circle>`
            : ''}
      ${this.loadingState === ClrLoadingState.default ? html `<slot></slot>` : ''}
    </div>`;
    }
    isDefaultLoadingState(state) {
        return state === ClrLoadingState.default;
    }
    disableButton() {
        this.style.width = getElementWidth(this);
        super.disabled = true;
    }
    restoreButton() {
        this.style.removeProperty('width');
        super.disabled = this._disabledExternally;
    }
}
CdsButton.styles = [baseStyles, styles];
__decorate([
    property({ type: String })
], CdsButton.prototype, "action", void 0);
__decorate([
    property({ type: String })
], CdsButton.prototype, "status", void 0);
__decorate([
    property({ type: String })
], CdsButton.prototype, "size", void 0);
__decorate([
    property({ type: Boolean })
], CdsButton.prototype, "block", void 0);
__decorate([
    property({ type: String })
], CdsButton.prototype, "loadingState", void 0);
__decorate([
    property({ type: Boolean })
], CdsButton.prototype, "disabled", null);
